import { filter, merge, tap } from 'rxjs';
import { store } from '@redux/store';
import uiSlice from '../redux/uiStateSlice';
import modalService from '../services/modalService';
import Overlay from './overlay';

const modal$ = modalService.getHtml();
const modalHtml = {
  modalContainer: document.querySelector('.modal__box'),
  previousHtml: null,
};
const disconnectedCallbacks = new Map();
const registeredCallbacks = new Map();
const overlay = new Overlay();

const onDisconnected = (callbackName) => {
  const callbacksDisconnected = disconnectedCallbacks.get(callbackName) ?? [];
  callbacksDisconnected.filter((fn) => typeof fn === 'function')?.forEach((fn) => fn());

  // A callback that's registered for the HTML Ready and the Disconnected events are assumed
  // to run together, so remove them from the map as the modal is removed from the HTML.
  if (disconnectedCallbacks.has(callbackName)) {
    disconnectedCallbacks.delete(callbackName);
    registeredCallbacks.delete(callbackName);
  }
};

const closeModal = () => {
  const modalInformation = store.getState().uiState?.modalInformation;
  const callbackName = modalInformation?.name;

  onDisconnected(callbackName);
  modalHtml.modalContainer.classList.remove('modal--expanded');
  overlay.remove();
  modalHtml.modalContainer.innerHTML = null;
  modalHtml.previousCallback = null;
  modalHtml.previousHtml = null;

  store.dispatch(uiSlice.actions.clearModal());

  // trigger a custom event that contains the information for the previously closed modal.
  const event = new CustomEvent('modalClosed', {
    detail: {
      modalInformation,
    },
  });

  setTimeout(() => {
    document.body.dispatchEvent(event);
  }, 100);
};

const disconnectCallback = (data) => {
  const name = data.name;
  const fn = data.fn;
  const existingCallbacks = disconnectedCallbacks.get(name) ?? [];
  const callbacks = [fn, ...existingCallbacks];
  disconnectedCallbacks.set(name, callbacks);
};

const openModal = (html) => {
  const callbackName = store.getState().uiState?.modalInformation?.name;
  const previouscallbackName = store.getState().uiState?.modalInformation?.previousName;
  const callbacksRegistered = registeredCallbacks.get(callbackName) ?? [];

  modalHtml.previousCallback = callbackName;
  modalHtml.previousHtml = modalHtml.modalContainer.innerHTML?.trim();

  if (modalHtml.previousHtml) {
    onDisconnected(previouscallbackName);
  }

  modalHtml.modalContainer.innerHTML = html.value;
  modalHtml.modalContainer.classList.add('modal--expanded');
  overlay.create();
  callbacksRegistered.filter((fn) => typeof fn === 'function')?.forEach((fn) => fn());
};

const registerCallback = (data) => {
  const name = data.name;
  const fn = data.fn;
  const existingCallbacks = registeredCallbacks.get(name) ?? [];
  const callbacks = [fn, ...existingCallbacks];
  registeredCallbacks.set(name, callbacks);
};

const setPreviousHtml = () => {
  onDisconnected(modalHtml.previousCallback);
  modalHtml.modalContainer.innerHTML = null;
  modalService.setHtml({ payload: modalHtml.previousHtml });
};

const close = modal$.pipe(
  filter((data) => data.action === 'clearHtml'),
  tap(closeModal)
);
const disconnect = modal$.pipe(
  filter((data) => data.action === 'onHtmlRemoved'),
  tap(disconnectCallback)
);
const open = modal$.pipe(
  filter((data) => data.action === 'setHtml'),
  tap(openModal)
);
const register = modal$.pipe(
  filter((data) => data.action === 'onHtmlReady'),
  tap(registerCallback)
);
const previous = modal$.pipe(
  filter((data) => data.action === 'setPreviousHtml'),
  tap(setPreviousHtml)
);
merge(close, disconnect, open, register, previous).subscribe();
